import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, Armchair, Grid3x3, UsersRound, Waves, Eye, UserCheck, MapPin, Smartphone, Shield, ChefHat, ChevronDown, ChevronRight, UtensilsCrossed, Tag, Salad, ExternalLink, Settings, Building, Brain, FileText, Database, UserCircle, CheckCircle, Home, Layers, BarChart3, ClipboardList, DollarSign } from 'lucide-react';

export const UserSidebar = ({ user }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(true);
  const [mastersOpen, setMastersOpen] = useState(false);
  const [smartIntelligenceOpen, setSmartIntelligenceOpen] = useState(false);
  
  const isActive = (path) => location.pathname === path;
  const isMenuActive = () => location.pathname.startsWith('/user/menu');
  const isMastersActive = () => ['/user/devices', '/user/roles', '/user/staff', '/user/seat-types', '/user/seats', '/user/sections', '/user/venues', '/user/configuration', '/user/guest-profiling', '/user/industry-benchmarks'].some(path => location.pathname === path);
  const isSmartIntelligenceActive = () => location.pathname.startsWith('/user/smart-intelligence');
  
  // Menu items for Organisation Admin
  const orgAdminMenu = [
    { name: 'Dashboard', path: '/user/dashboard', icon: LayoutDashboard },
    { name: 'Properties', path: '/user/properties', icon: Building2 }
  ];
  
  // Menu items for Property Admin (main items)
  const propertyAdminMenu = [
    { name: 'SmartView', path: '/user/smartview', icon: Eye },
    { name: 'Dashboard', path: '/user/dashboard', icon: LayoutDashboard },
    { name: 'Daily Guest List', path: '/user/guest-list', icon: UserCheck },
    { name: 'Allocation', path: '/user/allocation', icon: MapPin },
    { name: 'Staffing', path: '/user/staffing', icon: UsersRound }
  ];

  // Masters sub-items
  const mastersSubItems = [
    { name: 'Devices', path: '/user/devices', icon: Smartphone },
    { name: 'Roles', path: '/user/roles', icon: Shield },
    { name: 'Staff', path: '/user/staff', icon: Users },
    { name: 'Seat Types', path: '/user/seat-types', icon: Grid3x3 },
    { name: 'Seats', path: '/user/seats', icon: Armchair },
    { name: 'Sections', path: '/user/sections', icon: UsersRound },
    { name: 'Venues', path: '/user/venues', icon: Building },
    { name: 'Guest Profiling', path: '/user/guest-profiling', icon: UserCircle },
    { name: 'Industry Benchmarks', path: '/user/industry-benchmarks', icon: BarChart3 },
    { name: 'Configuration', path: '/user/configuration', icon: Settings }
  ];

  // Menu sub-items
  const menuSubItems = [
    { name: 'Categories', path: '/user/menu/categories', icon: ChefHat },
    { name: 'Tags', path: '/user/menu/tags', icon: Tag },
    { name: 'Dietary Restrictions', path: '/user/menu/dietary', icon: Salad },
    { name: 'Items', path: '/user/menu/items', icon: UtensilsCrossed },
    { name: 'Menus', path: '/user/menu/menus', icon: ChefHat },
    { name: 'Orders', path: '/user/menu/orders', icon: ClipboardList }
  ];
  
  // Select menu based on user type
  const menuItems = user?.entityType === 'organisation' ? orgAdminMenu : propertyAdminMenu;
  
  return (
    <div className="w-64 min-h-screen bg-slate-900 text-white flex flex-col">
      {/* Logo Section */}
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
            <Waves className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold">SmartFlags</h1>
            <p className="text-xs text-slate-400">
              {user?.entityType === 'organisation' ? 'Organisation Admin' : 'Property Admin'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isActive(item.path)
                      ? 'bg-teal-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              </li>
            );
          })}

          {/* Expandable Sections - Only for Property Admin */}
          {user?.entityType === 'property' && (
            <>
              {/* Masters Section */}
              <li>
                <button
                  onClick={() => setMastersOpen(!mastersOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                    isMastersActive()
                      ? 'bg-teal-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Database className="w-5 h-5" />
                    <span className="font-medium">Masters</span>
                  </div>
                  {mastersOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {/* Masters Sub-menu */}
                {mastersOpen && (
                  <ul className="mt-2 ml-4 space-y-1">
                    {mastersSubItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      return (
                        <li key={subItem.path}>
                          <Link
                            to={subItem.path}
                            className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                              isActive(subItem.path)
                                ? 'bg-teal-500 text-white'
                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                            }`}
                          >
                            <SubIcon className="w-4 h-4" />
                            <span>{subItem.name}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>

              {/* Menu Section */}
              <li>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                    isMenuActive()
                      ? 'bg-teal-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <ChefHat className="w-5 h-5" />
                    <span className="font-medium">Menu</span>
                  </div>
                  {menuOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {/* Sub-menu */}
                {menuOpen && (
                  <ul className="mt-2 ml-4 space-y-1">
                    {menuSubItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      return (
                        <li key={subItem.path}>
                          <Link
                            to={subItem.path}
                            className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                              isActive(subItem.path)
                                ? 'bg-teal-500 text-white'
                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                            }`}
                          >
                            <SubIcon className="w-4 h-4" />
                            <span>{subItem.name}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>

              {/* Smart Intelligence Section */}
              <li>
                <button
                  onClick={() => setSmartIntelligenceOpen(!smartIntelligenceOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                    isSmartIntelligenceActive()
                      ? 'bg-teal-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Brain className="w-5 h-5" />
                    <span className="font-medium">Smart Intelligence</span>
                  </div>
                  {smartIntelligenceOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                {/* Sub-menu */}
                {smartIntelligenceOpen && (
                  <ul className="mt-2 ml-4 space-y-1">
                    <li>
                      <Link
                        to="/user/smart-intelligence"
                        className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                          location.pathname === '/user/smart-intelligence'
                            ? 'bg-teal-500 text-white'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <Home className="w-4 h-4" />
                        <span>Home</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/user/smart-intelligence/proactive-checkins"
                        className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                          isActive('/user/smart-intelligence/proactive-checkins')
                            ? 'bg-teal-500 text-white'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Proactive Checkins</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/user/smart-intelligence/reports"
                        className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                          isActive('/user/smart-intelligence/reports')
                            ? 'bg-teal-500 text-white'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span>Overall Reports</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/user/smart-intelligence/staff-comparison"
                        className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                          isActive('/user/smart-intelligence/staff-comparison')
                            ? 'bg-teal-500 text-white'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <Users className="w-4 h-4" />
                        <span>Staff Comparison</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/user/smart-intelligence/section-comparison"
                        className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                          isActive('/user/smart-intelligence/section-comparison')
                            ? 'bg-teal-500 text-white'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <Layers className="w-4 h-4" />
                        <span>Section Comparison</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/user/smart-intelligence/venue-comparison"
                        className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                          isActive('/user/smart-intelligence/venue-comparison')
                            ? 'bg-teal-500 text-white'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <Building className="w-4 h-4" />
                        <span>Venue Comparison</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/user/smart-intelligence/revenue-report"
                        className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-all text-sm ${
                          isActive('/user/smart-intelligence/revenue-report')
                            ? 'bg-teal-500 text-white'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        }`}
                        data-testid="sidebar-revenue-report"
                      >
                        <DollarSign className="w-4 h-4" />
                        <span>Revenue Report</span>
                      </Link>
                    </li>
                  </ul>
                )}
              </li>
            </>
          )}
        </ul>
      </nav>

      {/* User Info Section */}
      <div className="p-4 border-t border-slate-800">
        <div className="px-4 py-3 bg-slate-800 rounded-lg">
          <p className="text-sm font-semibold text-white">{user?.name || 'User'}</p>
          <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
        </div>
      </div>
    </div>
  );
};
